import {
	Eye,
	FileText,
	FlaskConical,
	Package,
	Paperclip,
	Pencil,
	Plus,
	RefreshCw,
	Send,
	Trash2,
	Truck,
	XCircle,
} from 'lucide-react';
import type { ActivityNodeStyle } from './ActivityTimeline';

/**
 * Which icon and colour a quote activity gets, by its action.
 *
 * Presentation of a backend enum, so it belongs beside the timeline that draws
 * it rather than in whichever app happens to render one — the admin page and the
 * client view must not disagree about what a "batch sent" event looks like.
 */
/* ── Action → event type mapping ── */

/**
 * The backend emits 54 static actions plus two dynamic families, and `action` is
 * still typed `String!` (the activity enum is held pending an audit of the values
 * actually stored). So this stays a string map with an explicit fallback — see
 * `fallbackType` — rather than an exhaustive Record. Once the enum lands, this
 * becomes `Record<OrderQuoteActivityAction, EventType>` and the fallback goes.
 */
type EventType =
	| 'create'
	| 'update'
	| 'product'
	| 'offer'
	| 'send'
	| 'status'
	| 'cancel'
	| 'delete'
	| 'delivery'
	| 'sample'
	| 'attachment'
	| 'access';

const ACTION_TYPE: Record<string, EventType> = {
	QUOTE_CREATED: 'create',
	ORDER_CREATED: 'create',
	LATE_ACCEPT_CONFIRMED: 'create',

	QUOTE_UPDATED: 'update',
	PRICING_UPDATED: 'update',
	VETTER_ASSIGNED: 'update',
	ADJUSTMENT_ADDED: 'update',
	ADJUSTMENT_UPDATED: 'update',

	ITEMS_ADDED: 'product',
	ITEM_UPDATED: 'product',
	REQUEST_ITEMS_ADDED: 'product',
	REQUEST_ITEM_UPDATED: 'product',

	BATCH_CREATED: 'offer',
	BATCH_UPDATED: 'offer',
	BATCH_DUPLICATED: 'offer',
	PRODUCTS_ADDED_TO_OFFER: 'offer',
	OFFER_ADDED: 'offer',
	OFFER_UPDATED: 'offer',
	OFFER_REQUESTED: 'offer',
	LATE_ACCEPT_REVISION_STARTED: 'offer',

	BATCH_SENT_TO_CLIENT: 'send',
	OFFER_REVISED_RESENT: 'send',

	BATCH_RECALLED: 'status',
	BATCH_REJECTED: 'status',
	OFFERS_ACCEPTED: 'status',
	OFFERS_ACCEPTED_PENDING_REVIEW: 'status',
	LATE_ACCEPT_REJECTED: 'status',
	ORDER_CONFIRMED: 'status',

	ORDER_CANCELLED: 'cancel',

	QUOTE_DELETED: 'delete',
	BATCH_DELETED: 'delete',
	OFFER_REMOVED: 'delete',
	ITEM_REMOVED: 'delete',
	REQUEST_ITEM_REMOVED: 'delete',
	ADJUSTMENT_REMOVED: 'delete',
	DELIVERY_REMOVED: 'delete',
	DELIVERY_PLAN_REMOVED: 'delete',
	SAMPLE_REMOVED: 'delete',
	ATTACHMENT_DELETED: 'delete',
	VETTER_REMOVED: 'delete',

	DELIVERY_ADDED: 'delivery',
	DELIVERY_UPDATED: 'delivery',
	DELIVERY_PLAN_ADDED: 'delivery',
	DELIVERY_PLAN_UPDATED: 'delivery',
	DELIVERY_PLAN_DUPLICATED: 'delivery',
	SHIPMENT_CREATED: 'delivery',
	SHIPMENT_UPDATED: 'delivery',

	SAMPLE_ADDED: 'sample',
	SAMPLE_UPDATED: 'sample',
	SAMPLE_PRICE_UPDATED: 'sample',
	SAMPLE_NEW_ROUND_APPROVED: 'sample',
	SAMPLE_PAYMENT_TREATMENT_CHANGED: 'sample',

	ATTACHMENT_UPLOADED: 'attachment',
	/* A description edit, not a new file — the entry names which file it was,
	   since a product can carry several. Deleting still reads as a delete. */
	ATTACHMENT_UPDATED: 'attachment',

	PII_ACCESS: 'access',
};

const NODE_STYLE: Record<EventType, ActivityNodeStyle> = {
	create: {
		icon: <Plus size={12} />,
		fg: 'var(--accent)',
		ring: 'var(--accent-line)',
		bg: 'var(--accent-soft)',
	},
	update: {
		icon: <Pencil size={12} />,
		fg: 'var(--text-2)',
		ring: 'var(--line-3)',
		bg: 'var(--bg-4)',
	},
	product: {
		icon: <Package size={12} />,
		fg: 'var(--accent)',
		ring: 'var(--accent-line)',
		bg: 'var(--accent-soft)',
	},
	offer: {
		icon: <FileText size={12} />,
		fg: 'var(--status-warn)',
		ring: 'color-mix(in srgb, var(--status-warn) 38%, transparent)',
		bg: 'var(--status-warn-soft)',
	},
	send: {
		icon: <Send size={12} />,
		fg: 'var(--accent)',
		ring: 'var(--accent-line)',
		bg: 'var(--accent-soft)',
	},
	status: {
		icon: <RefreshCw size={12} />,
		fg: 'var(--status-info)',
		ring: 'color-mix(in srgb, var(--status-info) 42%, transparent)',
		bg: 'var(--status-info-soft)',
	},
	cancel: {
		icon: <XCircle size={12} />,
		fg: 'var(--status-danger)',
		ring: 'color-mix(in srgb, var(--status-danger) 32%, transparent)',
		bg: 'color-mix(in srgb, var(--status-danger) 10%, transparent)',
	},
	delete: {
		icon: <Trash2 size={12} />,
		fg: 'var(--status-danger)',
		ring: 'color-mix(in srgb, var(--status-danger) 32%, transparent)',
		bg: 'color-mix(in srgb, var(--status-danger) 10%, transparent)',
	},
	delivery: {
		icon: <Truck size={12} />,
		fg: 'var(--accent)',
		ring: 'var(--accent-line)',
		bg: 'var(--accent-soft)',
	},
	sample: {
		icon: <FlaskConical size={12} />,
		fg: 'var(--status-warn)',
		ring: 'color-mix(in srgb, var(--status-warn) 38%, transparent)',
		bg: 'var(--status-warn-soft)',
	},
	attachment: {
		icon: <Paperclip size={12} />,
		fg: 'var(--text-2)',
		ring: 'var(--line-3)',
		bg: 'var(--bg-4)',
	},
	access: {
		icon: <Eye size={12} />,
		fg: 'var(--text-2)',
		ring: 'var(--line-3)',
		bg: 'var(--bg-4)',
	},
};

/**
 * `ITEM_${status}` and `SAMPLE_${status}` are built from the status enums at the
 * call site, so those families are open by construction and can't be listed above.
 */
function fallbackType(action: string): EventType {
	if (action.startsWith('SAMPLE_')) return 'sample';
	if (action.startsWith('ITEM_')) return 'status';
	return 'update';
}

export const quoteActivityStyle = (action: string): ActivityNodeStyle =>
	NODE_STYLE[ACTION_TYPE[action] ?? fallbackType(action)];
